import type { StatusTone } from '../constants/statusTokens';
import type {
  FacilitySnapshot,
  OpsRouteRun,
  OpsRouteRunStop,
  RouteRunStatus,
  RouteRunStopStatus,
  TruckRecord,
  TruckStatus,
} from '../types/fleet.type';
import { lineCoordinates, toLngLat, type LngLat } from './geo';
import { parseServerTimestamp } from './serverTime';

export function runTone(status: RouteRunStatus): StatusTone {
  switch (status) {
    case 'in_progress':
      return 'active';
    case 'completed':
      return 'complete';
    case 'cancelled':
      return 'failed';
    default:
      return 'pending';
  }
}

export function stopTone(status: RouteRunStopStatus): StatusTone {
  switch (status) {
    case 'arrived':
      return 'active';
    case 'completed':
      return 'complete';
    case 'cancelled':
      return 'failed';
    default:
      return 'pending';
  }
}

const MOVING: ReadonlySet<TruckStatus> = new Set(['in_transit', 'loading', 'unloading']);

export function truckTone(truck: Pick<TruckRecord, 'status' | 'driver_id'>): StatusTone {
  if (truck.status === 'maintenance' || truck.status === 'unavailable') return 'offline';
  if (MOVING.has(truck.status)) return 'active';
  if (!truck.driver_id) return 'unassigned';
  return 'complete';
}

export type Freshness = 'recent' | 'aging' | 'stale' | 'unknown';

/** How old a truck's last recorded update is: ≤ 5 min recent, ≤ 30 min aging. */
export function truckFreshness(updatedAt: string | null | undefined, now: number): Freshness {
  const date = parseServerTimestamp(updatedAt);
  if (!date) return 'unknown';
  const minutes = (now - date.getTime()) / 60_000;
  if (minutes <= 5) return 'recent';
  if (minutes <= 30) return 'aging';
  return 'stale';
}

export interface RunProgress {
  total: number;
  completed: number;
  cancelled: number;
  arrived: number;
  /** Not yet completed or cancelled (pending + arrived). */
  remaining: number;
  /** Sum of reported weights on completed stops; null when none were reported. */
  weightKg: number | null;
}

export function runProgress(run: Pick<OpsRouteRun, 'stops'>): RunProgress {
  let completed = 0;
  let cancelled = 0;
  let arrived = 0;
  let weight = 0;
  let weighed = false;
  for (const stop of run.stops ?? []) {
    if (stop.status === 'completed') {
      completed += 1;
      if (typeof stop.weight_kg === 'number' && Number.isFinite(stop.weight_kg)) {
        weight += stop.weight_kg;
        weighed = true;
      }
    } else if (stop.status === 'cancelled') {
      cancelled += 1;
    } else if (stop.status === 'arrived') {
      arrived += 1;
    }
  }
  const total = run.stops?.length ?? 0;
  return {
    total,
    completed,
    cancelled,
    arrived,
    remaining: total - completed - cancelled,
    weightKg: weighed ? weight : null,
  };
}

export interface DayTotals {
  runs: number;
  byStatus: Record<RouteRunStatus, number>;
  stopsTotal: number;
  stopsCompleted: number;
  stopsCancelled: number;
  weightKg: number | null;
  plannedKm: number | null;
  /** Runs with an offload time — material delivered to the end facility. */
  delivered: number;
}

export function dayTotals(runs: readonly OpsRouteRun[]): DayTotals {
  const byStatus: Record<RouteRunStatus, number> = { pending: 0, in_progress: 0, completed: 0, cancelled: 0 };
  let stopsTotal = 0;
  let stopsCompleted = 0;
  let stopsCancelled = 0;
  let weight = 0;
  let weighed = false;
  let km = 0;
  let planned = false;
  let delivered = 0;
  for (const run of runs) {
    byStatus[run.status] = (byStatus[run.status] ?? 0) + 1;
    const p = runProgress(run);
    stopsTotal += p.total;
    stopsCompleted += p.completed;
    stopsCancelled += p.cancelled;
    if (p.weightKg !== null) {
      weight += p.weightKg;
      weighed = true;
    }
    if (typeof run.planned_distance_km === 'number' && run.status !== 'cancelled') {
      km += run.planned_distance_km;
      planned = true;
    }
    if (run.offload_at) delivered += 1;
  }
  return {
    runs: runs.length,
    byStatus,
    stopsTotal,
    stopsCompleted,
    stopsCancelled,
    weightKg: weighed ? weight : null,
    plannedKm: planned ? km : null,
    delivered,
  };
}

export function sortStops(stops: readonly OpsRouteRunStop[]): OpsRouteRunStop[] {
  return [...stops].sort((a, b) => a.sequence - b.sequence);
}

export function facilityPoint(facility: FacilitySnapshot | null | undefined): LngLat | null {
  return facility ? toLngLat(facility.latitude, facility.longitude) : null;
}

export function stopPoint(stop: OpsRouteRunStop): LngLat | null {
  return toLngLat(stop.point_snapshot?.latitude, stop.point_snapshot?.longitude);
}

export interface RunLine {
  coordinates: LngLat[];
  /** True when drawn from the TomTom-planned road geometry; false for stop order only. */
  planned: boolean;
}

/**
 * Line to draw for a run: the stored TomTom geometry when present, otherwise
 * straight segments start facility → stops in order → end facility (labelled
 * as stop order, never as a road route). Null when fewer than two points.
 */
export function runLine(run: OpsRouteRun): RunLine | null {
  const planned = lineCoordinates(run.planned_geometry);
  if (planned) return { coordinates: planned, planned: true };
  const points = [
    facilityPoint(run.route_snapshot?.start_facility),
    ...sortStops(run.stops ?? []).map(stopPoint),
    facilityPoint(run.route_snapshot?.end_facility),
  ].filter((p): p is LngLat => p !== null);
  return points.length >= 2 ? { coordinates: points, planned: false } : null;
}

/** Display name of a stop: point name, then supplier, then sequence. */
export function stopName(stop: OpsRouteRunStop): string {
  const s = stop.point_snapshot ?? {};
  return (s.name || s.supplier_name || '').toString().trim() || `#${stop.sequence}`;
}

const STATUS_ORDER: Record<RouteRunStatus, number> = { in_progress: 0, pending: 1, completed: 2, cancelled: 3 };

/** Runs in operational order: in progress, pending, completed, cancelled; then truck name. */
export function sortRuns(runs: readonly OpsRouteRun[]): OpsRouteRun[] {
  return [...runs].sort(
    (a, b) =>
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
      (a.truck_name ?? '').localeCompare(b.truck_name ?? '') ||
      (a.sequence ?? 0) - (b.sequence ?? 0),
  );
}
